// /studio/:cellPath — Phase E2 editing surface.
// Left pane: hierarchical cell tree (shared via CellTreeShell).
// Right pane: TemplateStudio with FsGitAdapter, loaded LIVE from projector
//   via GET /content/:path so edits-in-flight on disk show up on next nav.
//
// Auth gate (mirrors /admin's pattern verbatim):
//   Server-side ACL is the real enforcement (apps/projector/src/routes/studio.ts).
//   We render a thin "sign in" gate on the only signal useAuth() exposes —
//   `authenticated` — so unauthenticated users see a clear message rather
//   than save/diff calls failing deep inside TemplateStudio. We do NOT add
//   a client-side role check; the projector is the source of truth.
//   `DEV_AUTH_BYPASS=true` follows the same pattern as Layout.tsx + api.ts.

// MDXEditor's stylesheet must load before tailwind so design-system
// tokens win on any conflicts. Imported here (not main.tsx) so it is
// only fetched when an admin navigates to /studio — keeping it out
// of the investor entry bundle.
import '@mdxeditor/editor/style.css';
import { useEffect, useMemo, useState } from 'react';
import { TemplateStudio, FsGitAdapter } from '@rmint/editor';
import { useAuth } from '@rmint/shell';
import { env } from '@rmint/shell';
import { encodeCellPath } from '../lib/cell-loader';
import { CellTreeShell } from '../components/CellTreeShell';

// Inner pane: live-fetches cell source and renders TemplateStudio.
// Auth gate lives here too — Studio is the only route that needs it.
function StudioPane({ cellPath }: { cellPath: string }) {
  const { ready, authenticated } = useAuth();
  // FsGitAdapter is stateless apart from baseUrl; one instance per mount.
  // baseUrl '' (same-origin) is the prod posture; dev points at :8090.
  const adapter = useMemo(() => new FsGitAdapter(env.apiBaseUrl), []);
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSource(null);
    setError(null);
    if (!cellPath) return;
    let cancelled = false;
    // Live fetch — Studio needs on-disk bytes, not cell-loader's snapshot,
    // because users may have just saved via /studio/save in this session.
    const url = `${env.apiBaseUrl}/content/${encodeCellPath(cellPath)}`;
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`${r.status}`))))
      .then((t) => {
        if (!cancelled) setSource(t);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [cellPath]);

  const allowed = authenticated || env.devAuthBypass;

  if (!ready) return <p className="text-foreground-soft">Checking session…</p>;
  if (!allowed) {
    return (
      <p className="p-6">
        Studio requires admin role. Sign in with an admin account to edit cells.
      </p>
    );
  }
  if (!cellPath) return <p>Pick a cell from the tree on the left.</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (source === null) return <p className="text-foreground-soft">Loading…</p>;
  return (
    <TemplateStudio
      key={cellPath}
      adapter={adapter}
      // Prepend "content/" — projector's isAllowedPath requires content/,
      // meta/, or packages/core/states/ prefixes.
      cellPath={`content/${cellPath}`}
      initialSource={source}
    />
  );
}

export function Studio() {
  return <CellTreeShell rightPane={StudioPane} routePrefix="studio" />;
}
